import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { tokens } from '../styles/tokens';
import { shared } from '../styles/shared';
import { STRINGS } from '../data/strings';

export type SarahInscriptionMode = 'recap' | 'modal';

@customElement('sarah-inscription')
export class SarahInscription extends LitElement {
  static styles = [
    tokens,
    shared,
    css`
      :host { display: block; }

      .inscription {
        box-shadow: 0 1px 11px 2px #0000001a, 0 1px 2px 0 #0000000f !important;
        padding: 40px;
        border: 2px solid var(--sarah-rose);
        margin-top: -20px;
        background: #fff;
        z-index: 99;
        position: relative;
        border-radius: 15px;
        text-align: center;
        font-family: var(--sarah-font-body);
        color: var(--sarah-text);
      }
      .inscription.modal {
        box-shadow: none !important;
        border: 2px solid var(--sarah-rose);
        margin-top: 0 !important;
      }
      @media (max-width: 767px) {
        .inscription { padding: 20px 16px; margin-top: -90px; }
        .inscription.modal { margin-top: 0 !important; }
      }
      .pre-title {
        text-align: center;
        font-size: 16px;
        text-transform: uppercase;
        margin: 0;
      }
      .pre-title span { font-weight: 600; }
      @media (max-width: 719px) {
        .pre-title { font-size: 14px; margin-bottom: 10px; }
      }
      .title {
        font-family: var(--sarah-font-display);
        font-size: 28px;
        color: var(--sarah-bleu);
        margin: 15px 0;
        font-weight: 800;
        text-transform: uppercase;
      }
      @media (max-width: 719px) { .title { font-size: 20px; } }
      .sub-title {
        font-size: 22px;
        color: var(--sarah-rose);
        font-weight: 600;
        margin: 30px 0;
      }
      .sub-title span { font-weight: 700; }
      @media (max-width: 719px) {
        .sub-title { margin: 15px 0; line-height: 1.25; font-size: 16px; }
      }

      form {
        margin-top: 50px;
        display: flex;
        flex-wrap: wrap;
        gap: 16px;
        align-items: flex-end;
      }
      form .field {
        width: 100%;
        flex: 1 1 200px;
        margin-bottom: 0;
        text-align: left;
      }
      .inscription-button { flex: 0 1 auto; }
      .inscription-button .btn {
        height: 56px;
        min-width: 180px;
        font-family: var(--sarah-font-display);
        font-weight: 700;
        font-size: 16px;
        line-height: 24px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        margin: 0;
        padding: 0 24px;
        border: none;
        border-radius: 3px;
        color: #fff;
        text-align: center;
        text-decoration: none;
        background-color: var(--sarah-rose);
        outline: 0;
        cursor: pointer;
        white-space: nowrap;
      }
      .inscription-button .btn:hover { background-color: var(--sarah-rose-hover); }
      .inscription-button .btn:disabled {
        color: var(--sarah-disabled-fg) !important;
        background-color: var(--sarah-disabled-bg) !important;
        cursor: not-allowed !important;
      }
      @media (max-width: 599px) {
        .inscription-button { flex: 1 1 100%; }
        .inscription-button .btn { width: 100%; }
      }

      .card-error {
        background: #e70a0a;
        color: #fff;
        text-align: left;
        font-size: 16px;
        font-weight: 500;
        padding: 10px 15px;
        margin-top: 12px;
        border-radius: 6px;
        animation: simpleFade .6s ease;
      }

      .connected-state {
        padding: 20px 0;
        font-size: 18px;
        color: var(--sarah-bleu);
        font-weight: 700;
      }
      .connected-state .redirect {
        margin-top: 12px;
        color: var(--sarah-rose);
        font-weight: 600;
        font-size: 16px;
      }

      @keyframes simpleFade {
        from { opacity: 0; }
        to   { opacity: 1; }
      }
    `,
  ];

  @property({ type: String }) mode: SarahInscriptionMode = 'recap';
  @property({ type: Boolean, attribute: false }) connected = false;
  @property({ type: String, attribute: false }) errorMessage = '';

  @state() private submitting = false;
  @query('input[name="formEmail"]') private emailInput?: HTMLInputElement;
  @query('input[name="formPassword"]') private passwordInput?: HTMLInputElement;

  private onSubmit(e: Event) {
    e.preventDefault();
    if (this.submitting) return;

    const email = this.emailInput?.value?.trim() || '';
    const password = this.passwordInput?.value || '';
    if (!email || !password) return;

    this.submitting = true;
    this.errorMessage = '';
    this.dispatchEvent(
      new CustomEvent('login-submit', {
        detail: { email, password },
        bubbles: true,
        composed: true,
      }),
    );
  }

  /**
   * Le parent réinjecte ces props après la réponse API. On déverrouille le
   * bouton dès qu'une erreur arrive ou que le composant repasse en non-connecté.
   */
  updated(changed: Map<string, unknown>) {
    if (changed.has('errorMessage') && this.errorMessage) {
      this.submitting = false;
    }
    if (changed.has('connected') && this.connected) {
      this.submitting = false;
    }
  }

  render() {
    const klass = this.mode === 'modal' ? 'inscription modal' : 'inscription';

    if (this.connected) {
      return html`
        <div class=${klass}>
          <p class="pre-title">${STRINGS.inscriptionConnected}</p>
          <div class="connected-state">
            <p>${STRINGS.inscriptionConnected}</p>
            <p class="redirect">${STRINGS.inscriptionRedirect}</p>
          </div>
        </div>
      `;
    }

    return html`
      <div class=${klass}>
        <p class="pre-title">
          <span>${STRINGS.inscriptionPreTitle}</span> dans votre espace.
        </p>
        <h2 class="title">${STRINGS.inscriptionTitle}</h2>
        <p class="sub-title">${unsafeHTML(STRINGS.inscriptionSubTitle)}</p>
        <form novalidate autocomplete="off" @submit=${this.onSubmit}>
          <div class="field">
            <label>Votre email</label>
            <input type="email" name="formEmail" required ?disabled=${this.submitting}>
          </div>
          <div class="field">
            <label>Votre mot de passe</label>
            <input type="password" name="formPassword" required ?disabled=${this.submitting}>
          </div>
          <div class="inscription-button">
            <button type="submit" class="btn" ?disabled=${this.submitting}>
              ${STRINGS.btnConnect}
            </button>
          </div>
        </form>
        ${this.errorMessage
          ? html`<div class="card-error">${this.errorMessage}</div>`
          : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sarah-inscription': SarahInscription;
  }
}
